import { z } from 'zod';
import { aspectRatioDimensions, CAMPAIGN_STATUSES, LENGTHS, MEDIA_ASPECT_RATIOS, MEDIA_KINDS, MEDIA_LIMITS, MEDIA_STATUSES, METRIC_KEYS, PAYMENT_METHODS, PAYMENT_STATUSES, PLANS, PLATFORM_IDS, POST_STATUSES, REFERRAL_STATUSES, SUBSCRIPTION_STATUSES, TONES, } from './constants.js';
export const platformIdSchema = z.enum(PLATFORM_IDS);
export const postStatusSchema = z.enum(POST_STATUSES);
export const campaignStatusSchema = z.enum(CAMPAIGN_STATUSES);
export const toneSchema = z.enum(TONES);
export const lengthSchema = z.enum(LENGTHS);
export const metricKeySchema = z.enum(METRIC_KEYS);
export const registerSchema = z.object({
    email: z
        .string()
        .email('Enter a valid email address')
        .max(320, 'Email is too long'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(128, 'Password is too long'),
    name: z.string().min(1).max(120, 'Name is too long').optional(),
    /** Invite code from a `?ref=` signup link; attributed on a best-effort basis. */
    referralCode: z
        .string()
        .trim()
        .toUpperCase()
        .min(4, 'Referral code must be at least 4 characters')
        .max(32, 'Referral code is too long')
        .regex(/^[A-Z0-9-]+$/, 'Referral codes use letters, digits and dashes')
        .optional(),
});
export const loginSchema = z.object({
    email: z.string().email('Enter a valid email address').max(320, 'Email is too long'),
    password: z.string().min(1, 'Password is required').max(128, 'Password is too long'),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(10, 'Refresh token is required'),
});
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const aiOptionsSchema = z.object({
    tone: toneSchema.optional(),
    length: lengthSchema.optional(),
    hashtags: z.boolean().optional(),
    variants: z.number().int().min(1).max(5).optional(),
    keywords: z.array(z.string().min(1).max(60)).max(20).optional(),
    imagePrompt: z.string().min(3).max(600).optional(),
});
export const createPostSchema = z.object({
    title: z.string().min(1).max(200, 'Title must be 200 characters or fewer').optional(),
    draft: z
        .string()
        .min(1, 'Draft (caption) is required')
        .max(20_000, 'Draft is too long'),
    platformIds: z
        .array(platformIdSchema)
        .min(1, 'Select at least one platform')
        .max(PLATFORM_IDS.length, 'Too many platforms selected'),
    mediaUrls: z.array(z.string().url('Media URL must be a valid link')).max(10, 'Too many media URLs').optional(),
    /** Library assets to attach (resolved to URLs server-side). */
    mediaAssetIds: z.array(z.string().uuid('Media asset id must be valid')).max(10, 'Too many media assets').optional(),
    aiOptions: aiOptionsSchema.optional(),
    publishAt: z
        .string()
        .datetime({ offset: true, message: 'Publish date must be a valid date/time' })
        .optional(),
    requireApproval: z.boolean().optional(),
    campaignId: z.string().uuid('Campaign id must be valid').optional(),
});
export const updatePostSchema = z
    .object({
    title: z.string().min(1).max(200).nullable().optional(),
    draft: z.string().min(1).max(20_000).optional(),
    platformIds: z.array(platformIdSchema).min(1).optional(),
    mediaUrls: z.array(z.string().url()).max(10).optional(),
    mediaAssetIds: z.array(z.string().uuid()).max(10).optional(),
    publishAt: z.string().datetime({ offset: true }).nullable().optional(),
    requireApproval: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided',
});
export const listPostsQuerySchema = paginationSchema.extend({
    status: postStatusSchema.optional(),
    platform: platformIdSchema.optional(),
    campaignId: z.string().uuid().optional(),
    search: z.string().min(1).max(200).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
export const createCampaignSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(1000).optional(),
    platformIds: z.array(platformIdSchema).min(1),
    goal: z.enum(['awareness', 'engagement', 'leadgen', 'traffic', 'sales']).optional(),
    durationDays: z.number().int().min(1).max(90).optional(),
    startAt: z.string().datetime({ offset: true }).optional(),
    postsPerWeek: z.number().int().min(1).max(21).optional(),
    autoSchedule: z.boolean().optional(),
    aiBrief: z
        .object({
        objective: z.string().max(500).optional(),
        audience: z.string().max(500).optional(),
        keywords: z.array(z.string().min(1).max(60)).max(20).optional(),
        tone: toneSchema.optional(),
        competitorBrands: z.array(z.string().min(1).max(80)).max(10).optional(),
        constraints: z.string().max(500).optional(),
    })
        .optional(),
});
/**
 * Partial update for an existing campaign — every field is optional so the
 * client can send only what changed. Mirrors `createCampaignSchema`.
 */
export const updateCampaignSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(1000).nullable().optional(),
    goal: z.enum(['awareness', 'engagement', 'leadgen', 'traffic', 'sales']).optional(),
    platformIds: z.array(platformIdSchema).min(1).optional(),
    durationDays: z.number().int().min(1).max(90).optional(),
    startAt: z.string().datetime({ offset: true }).optional(),
    postsPerWeek: z.number().int().min(1).max(21).optional(),
    autoSchedule: z.boolean().optional(),
    aiBrief: z
        .object({
        objective: z.string().max(500).optional(),
        audience: z.string().max(500).optional(),
        keywords: z.array(z.string().min(1).max(60)).max(20).optional(),
        tone: toneSchema.optional(),
        competitorBrands: z.array(z.string().min(1).max(80)).max(10).optional(),
        constraints: z.string().max(500).optional(),
    })
        .optional(),
});
export const listCampaignsQuerySchema = paginationSchema.extend({
    status: campaignStatusSchema.optional(),
    search: z.string().min(1).max(200).optional(),
});
export const aiGenerateSchema = z.object({
    prompt: z.string().min(3).max(4000),
    tone: toneSchema.optional(),
    length: lengthSchema.optional(),
    platforms: z.array(platformIdSchema).max(PLATFORM_IDS.length).optional(),
    hashtags: z.boolean().optional(),
    variants: z.number().int().min(1).max(5).optional(),
    keywords: z.array(z.string().min(1).max(60)).max(20).optional(),
    language: z.string().min(2).max(10).optional(),
});
export const aiImageSchema = z.object({
    prompt: z.string().min(3).max(600),
    width: z.number().int().min(256).max(2048).optional(),
    height: z.number().int().min(256).max(2048).optional(),
    steps: z.number().int().min(1).max(80).optional(),
    seed: z.number().int().optional(),
    provider: z.string().trim().min(1).max(64).optional(),
    model: z.string().trim().min(1).max(128).optional(),
});
export const campaignInsightsSchema = z.object({
    campaignId: z.string().uuid(),
    focus: z.enum(['topics', 'schedule', 'audience', 'creatives', 'competitors']).optional(),
});
export const analyticsRangeSchema = z.object({
    days: z.coerce.number().int().min(1).max(365).default(30),
});
export const exportAnalyticsSchema = analyticsRangeSchema.extend({
    format: z.enum(['csv', 'json']).default('json'),
});
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const planIdSchema = z.enum(Object.keys(PLANS));
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export const referralStatusSchema = z.enum(REFERRAL_STATUSES);
/**
 * Start a checkout for a paid plan over UPI or card.
 *
 * The amount is never accepted from the client — the server prices the plan and
 * subtracts referral credit, so a tampered payload cannot change what is charged.
 */
export const createCheckoutSchema = z.object({
    plan: planIdSchema,
    method: paymentMethodSchema,
    /** Spends referral credit on this order. Defaults to true when any is available. */
    useCredit: z.boolean().optional(),
});
export const checkoutParamsSchema = z.object({ id: z.string().uuid() });
/**
 * Client-side completion callback. Every field is re-verified against Razorpay
 * server-side before a subscription is granted.
 */
export const verifyCheckoutSchema = z.object({
    razorpayPaymentId: z.string().min(4).max(128),
    razorpayOrderId: z.string().min(4).max(128),
    razorpaySignature: z.string().min(8).max(256),
});
export const applyReferralSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(4)
        .max(32)
        .regex(/^[A-Z0-9-]+$/, 'Referral codes use letters, digits and dashes'),
});
// ---------------------------------------------------------------------------
// Media library — image + video generation, listing and post attachment
// ---------------------------------------------------------------------------
export const mediaKindSchema = z.enum(MEDIA_KINDS);
export const mediaStatusSchema = z.enum(MEDIA_STATUSES);
export const mediaAspectRatioSchema = z.enum(MEDIA_ASPECT_RATIOS);
export const listMediaQuerySchema = paginationSchema.extend({
    kind: mediaKindSchema.optional(),
    status: mediaStatusSchema.optional(),
    search: z.string().min(1).max(200).optional(),
    /** Only assets attached to this post. */
    postId: z.string().uuid().optional(),
});
const promptSchema = z.string().trim().min(3).max(MEDIA_LIMITS.maxVideoPromptChars);
/**
 * Image generation request. `aspectRatio` wins over an explicit width/height so
 * the UI can offer presets while power users still pass pixel sizes.
 */
export const generateImageMediaSchema = z
    .object({
    prompt: z.string().trim().min(3).max(MEDIA_LIMITS.maxImagePromptChars),
    aspectRatio: mediaAspectRatioSchema.optional(),
    width: z.number().int().min(256).max(MEDIA_LIMITS.maxImageWidth).optional(),
    height: z.number().int().min(256).max(MEDIA_LIMITS.maxImageHeight).optional(),
    steps: z.number().int().min(1).max(80).optional(),
    seed: z.number().int().optional(),
    count: z.number().int().min(1).max(MEDIA_LIMITS.maxAssetsPerRequest).optional(),
    postId: z.string().uuid().optional(),
    provider: z.string().trim().min(1).max(64).optional(),
    model: z.string().trim().min(1).max(128).optional(),
})
    .transform((value) => {
    const preset = value.aspectRatio ? aspectRatioDimensions(value.aspectRatio) : null;
    return {
        ...value,
        width: preset?.width ?? value.width,
        height: preset?.height ?? value.height,
        count: value.count ?? 1,
    };
});
export const generateVideoMediaSchema = z
    .object({
    prompt: promptSchema,
    negativePrompt: z.string().trim().max(MEDIA_LIMITS.maxNegativePromptChars).optional(),
    aspectRatio: mediaAspectRatioSchema.optional(),
    durationSeconds: z
        .number()
        .int()
        .min(MEDIA_LIMITS.minVideoDurationSeconds)
        .max(MEDIA_LIMITS.maxVideoDurationSeconds)
        .optional(),
    fps: z.number().int().min(8).max(30).optional(),
    steps: z.number().int().min(2).max(60).optional(),
    seed: z.number().int().optional(),
    postId: z.string().uuid().optional(),
    provider: z.string().trim().min(1).max(64).optional(),
    model: z.string().trim().min(1).max(128).optional(),
})
    .transform((value) => {
    const preset = value.aspectRatio ? aspectRatioDimensions(value.aspectRatio) : null;
    return {
        ...value,
        width: preset?.width,
        height: preset?.height,
    };
});
/** Attach an existing library asset to a post (the "pick from the list" action). */
export const attachMediaSchema = z.object({
    postId: z.string().uuid(),
});
/** Payload for the stateless `POST /ai/videos` endpoint (no library persistence). */
export const aiVideoSchema = z.object({
    prompt: promptSchema,
    negativePrompt: z.string().trim().max(MEDIA_LIMITS.maxNegativePromptChars).optional(),
    width: z.number().int().min(256).max(MEDIA_LIMITS.maxVideoWidth).optional(),
    height: z.number().int().min(256).max(MEDIA_LIMITS.maxVideoHeight).optional(),
    durationSeconds: z.number().int().min(1).max(MEDIA_LIMITS.maxVideoDurationSeconds).optional(),
    fps: z.number().int().min(8).max(30).optional(),
    steps: z.number().int().min(2).max(60).optional(),
    seed: z.number().int().optional(),
    provider: z.string().trim().min(1).max(64).optional(),
    model: z.string().trim().min(1).max(128).optional(),
});
